import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, UserIcon, ClockIcon, MessageSquareIcon, AlertTriangleIcon, StickyNoteIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthContext';
import { FollowUpNote } from '../../types';
export function FollowUpNotes() {
  const {
    followUpNotes,
    addFollowUpNote,
    updateFollowUpNote,
    deleteFollowUpNote
  } = useData();
  const {
    canView,
    canCreate,
    canEdit,
    canDelete
  } = usePermissions();
  const {
    user
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<FollowUpNote | null>(null);
  const [formData, setFormData] = useState({
    contactName: '',
    subject: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Permission check
  if (!canView('followUpNotes')) {
    return <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6" data-id="element-1169">
        <MessageSquareIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-1170" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-1171">
          Acceso Restringido
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-1172">
          No tienes permisos para ver las notas de seguimiento.
        </p>
      </div>;
  }
  // Filter notes
  const filteredNotes = followUpNotes.filter(note => {
    const term = searchTerm.toLowerCase();
    return note.contactName.toLowerCase().includes(term) || note.subject.toLowerCase().includes(term) || note.description.toLowerCase().includes(term) || note.userName.toLowerCase().includes(term);
  });
  // Pagination
  const {
    paginatedItems,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredNotes, {
    itemsPerPage: 10
  });
  const openNewForm = () => {
    setSelectedNote(null);
    setFormData({
      contactName: '',
      subject: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (note: FollowUpNote) => {
    setSelectedNote(note);
    setFormData({
      contactName: note.contactName,
      subject: note.subject,
      description: note.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (note: FollowUpNote) => {
    setSelectedNote(note);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.contactName.trim()) errors.contactName = 'El nombre del contacto es requerido';
    if (!formData.subject.trim()) errors.subject = 'El asunto es requerido';
    if (!formData.description.trim()) errors.description = 'La descripción es requerida';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedNote) {
      await updateFollowUpNote(selectedNote.id, formData);
    } else {
      await addFollowUpNote({
        userId: user?.id || '',
        userName: user?.name || 'Usuario',
        ...formData
      });
    }
    setIsFormOpen(false);
  };
  const handleDelete = async () => {
    if (selectedNote) {
      await deleteFollowUpNote(selectedNote.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const formatDateTime = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' • ' + d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-6" data-id="element-1173">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1174">
        <div data-id="element-1175">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1176">
            Notas de Seguimiento
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1177">
            Registro de comunicaciones y seguimientos con contactos
          </p>
        </div>
        {canCreate('followUpNotes') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1179" />} onClick={openNewForm} data-id="element-1178">
            Nueva Nota
          </Button>}
      </div>

      {/* Search */}
      <Card data-id="element-1180">
        <div className="relative" data-id="element-1181">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1182" />
          <Input placeholder="Buscar por contacto, asunto o descripción..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1183" />
        </div>
      </Card>

      {/* Notes Table */}
      <Card padding="none" data-id="element-1184">
        <div className="overflow-x-auto" data-id="element-1185">
          <table className="w-full" data-id="element-1186">
            <thead data-id="element-1187">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-1188">
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1189">
                  Fecha y Hora
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1190">
                  Registrado por
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1191">
                  Contacto
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1192">
                  Asunto
                </th>
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1193">
                  Descripción
                </th>
                <th className="text-right py-3 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1194">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1195">
              {paginatedItems.length === 0 ? <tr data-id="element-1196">
                  <td colSpan={6} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1197">
                    <div className="flex flex-col items-center justify-center" data-id="element-1198">
                      <StickyNoteIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" data-id="element-1199" />
                      <p className="font-medium" data-id="element-1200">No hay notas de seguimiento</p>
                      <p className="text-sm mt-1" data-id="element-1201">
                        Crea una nueva nota para registrar tus comunicaciones
                      </p>
                    </div>
                  </td>
                </tr> : paginatedItems.map(note => <tr key={note.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-1202">
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-1203">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-1204">
                        <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-1205" />
                        <span data-id="element-1206">{formatDateTime(note.createdAt)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-1207">
                      <div className="flex items-center gap-2" data-id="element-1208">
                        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400" data-id="element-1209">
                          <UserIcon className="w-4 h-4" data-id="element-1210" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1211">
                          {note.userName}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-1212">
                      <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1213">
                        {note.contactName}
                      </span>
                    </td>
                    <td className="py-4 px-6" data-id="element-1214">
                      <Badge variant="info" data-id="element-1215">{note.subject}</Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-1216">
                      <p className="text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate" title={note.description} data-id="element-1217">
                        {note.description}
                      </p>
                    </td>
                    <td className="py-4 px-6" data-id="element-1218">
                      <div className="flex items-center justify-end gap-1" data-id="element-1219">
                        {canEdit('followUpNotes') && <button onClick={() => openEditForm(note)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-1220">
                            <EditIcon className="w-4 h-4" data-id="element-1221" />
                          </button>}
                        {canDelete('followUpNotes') && <button onClick={() => openDeleteConfirm(note)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar" data-id="element-1222">
                            <TrashIcon className="w-4 h-4" data-id="element-1223" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-1224">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-1225" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedNote ? 'Editar Nota' : 'Nueva Nota de Seguimiento'} size="md" icon={<MessageSquareIcon className="w-5 h-5" data-id="element-1227" />} accentColor="amber" data-id="element-1226">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1228">
          {/* Note Info */}
          <div data-id="element-1229">
            <div className="flex items-center gap-2 mb-4" data-id="element-1230">
              <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-1231"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1232">
                Información de la Nota
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1233">
              <Input label="Nombre del Contacto" value={formData.contactName} onChange={e => setFormData({
              ...formData,
              contactName: e.target.value
            })} error={formErrors.contactName} placeholder="Ej: Juan Pérez, Dr. García..." data-id="element-1234" />
              <Input label="Asunto" value={formData.subject} onChange={e => setFormData({
              ...formData,
              subject: e.target.value
            })} error={formErrors.subject} placeholder="Ej: Llamada de seguimiento, Correo enviado..." data-id="element-1235" />
            </div>
          </div>

          {/* Description */}
          <div data-id="element-1236">
            <div className="flex items-center gap-2 mb-4" data-id="element-1237">
              <span className="w-1.5 h-5 rounded-full bg-orange-500" data-id="element-1238"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1239">
                Descripción
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1240">
              <Textarea label="Detalle del seguimiento" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} error={formErrors.description} placeholder="Ej: Le escribí al paciente pero no respondió, se intentará contactar mañana..." rows={4} data-id="element-1241" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1242">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1243">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1244">
              {selectedNote ? 'Guardar Cambios' : 'Crear Nota'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Eliminar Nota" size="sm" data-id="element-1245">
        <div className="space-y-4" data-id="element-1246">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-1247">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-1248" />
            <div data-id="element-1249">
              <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-1250">
                ¿Eliminar esta nota de seguimiento?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-1251">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          {selectedNote && <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-1252">
              <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1253">
                {selectedNote.contactName}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1254">
                {selectedNote.subject}
              </p>
            </div>}

          <div className="flex justify-end gap-3 pt-2" data-id="element-1255">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1256">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1257">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}